import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useParams } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { ChevronLeft } from "lucide-react";
import { toast } from "sonner";

import { Skeleton } from "@/components/ui/skeleton";

import { getWorkPackage, patchTask } from "@/api/plan";
import { canEdit } from "@/components/chat/railMode";
import { DeskScreen } from "@/components/plan/DeskScreen";
import { LinkedIntegrations } from "@/components/plan/LinkedIntegrations";
import { useDocumentTitle } from "@/lib/document-title";

export const Route = createFileRoute("/_app/plan/p/$projectSlug/wp/$wpId/t/$taskId")({
  component: TaskDetail,
});

function TaskDetail() {
  const { projectSlug, wpId, taskId } = useParams({
    from: "/_app/plan/p/$projectSlug/wp/$wpId/t/$taskId",
  });
  const queryClient = useQueryClient();
  const wpKey = ["plan", "wp", wpId];

  const detail = useQuery({ queryKey: wpKey, queryFn: () => getWorkPackage(wpId) });

  const toggle = useMutation({
    mutationFn: (done: boolean) => patchTask(taskId, { done }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: wpKey });
      void queryClient.invalidateQueries({ queryKey: ["plan", "project-by-slug", projectSlug] });
      void queryClient.invalidateQueries({ queryKey: ["plan", "projects"] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const crumb = (
    <>
      <Link to="/plan/p/$projectSlug/wp/$wpId" params={{ projectSlug, wpId }} className="back">
        <ChevronLeft className="size-3.5" />
        Work Package
      </Link>
      <div className="crumb">Task</div>
    </>
  );

  const taskText = detail.data?.outcomes
    .flatMap(({ tasks }) => tasks)
    .find((t) => t.id === taskId)?.text;
  useDocumentTitle([taskText ?? "Task", detail.data?.work_package.title]);

  if (detail.isLoading) {
    return (
      <DeskScreen narrow>
        {crumb}
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-24 w-full" />
      </DeskScreen>
    );
  }

  // Locate the task + its parent outcome inside the work-package detail.
  const found = detail.data?.outcomes
    .flatMap(({ outcome, tasks }) => tasks.map((t) => ({ task: t, outcome })))
    .find((x) => x.task.id === taskId);

  if (detail.isError || !detail.data || !found) {
    return (
      <DeskScreen narrow>
        {crumb}
        <p className="text-[13px] text-[var(--color-semantic-error)]">
          {(detail.error as Error)?.message ?? "Task not found."}
        </p>
      </DeskScreen>
    );
  }

  const { task, outcome } = found;
  const { work_package: wp } = detail.data;
  // Write gate (editor+). Mirrors the WP-list task toggle and the backend
  // WorkspaceRole.EDITOR floor on PATCH /tasks/{id}. Viewers/commenters see
  // the status read-only.
  const mayEdit = canEdit(detail.data.my_role);

  return (
    <DeskScreen narrow>
      {crumb}

      {/* Header — done state pill above the task text (mockup detail shell). */}
      <span className={`status-pill lg ${task.done ? "on" : "neutral"} wp-status`}>
        <span className="sp-dot" /> {task.done ? "Done" : "To do"}
      </span>
      <h1 className="wpx-title">{task.text}</h1>

      <div className="wpx-meta">
        <span className="meta-pair">
          <span className="meta-k">Outcome</span>
          {outcome.text}
        </span>
        <span className="meta-pair">
          <span className="meta-k">Work package</span>
          <Link
            to="/plan/p/$projectSlug/wp/$wpId"
            params={{ projectSlug, wpId }}
            className="text-[var(--color-brand-terracotta-deep)] hover:underline"
          >
            {wp.title}
          </Link>
        </span>
        {task.estimate_hours != null && (
          <span className="meta-pair">
            <span className="meta-k">Estimate</span>
            {task.estimate_hours}h
          </span>
        )}
        {task.done && task.completed_at && (
          <span className="meta-pair">
            <span className="meta-k">Completed</span>
            {formatDistanceToNow(new Date(task.completed_at), { addSuffix: true })}
          </span>
        )}
      </div>

      {/* Primary action — mark complete / reopen (editor+ only). Viewers and
          commenters see the read-only status pill in the header above. */}
      {mayEdit && (
        <div className="mt-4">
          <button
            className={`btn ${task.done ? "" : "primary"} sm`}
            disabled={toggle.isPending}
            onClick={() => toggle.mutate(!task.done)}
          >
            {task.done ? "↺ Reopen task" : "✓ Mark complete"}
          </button>
        </div>
      )}

      <LinkedIntegrations objectKind="task" objectId={taskId} canEdit={mayEdit} />
    </DeskScreen>
  );
}
